import { useEffect, useMemo, useState } from "react";
import { createCnoPatient, getCnoPatients, updateCnoPatient } from "../../../services/api";
import { migrateLegacyPatients, readLegacyPatients, removeLegacyPatients } from "../model/cnoLegacyPatients";
import {
    emptyPatient,
    normalizePatients,
    patchPatient,
    reconcileSelectedPatientId,
    replacePatient,
    replaceStatus,
    saveKeyForPatient,
    withStatus,
} from "../model/cnoRules";

async function fetchPatients() {
    const response = await getCnoPatients();
    let loaded = normalizePatients(response.data);
    const legacyPatients = readLegacyPatients();
    if (loaded.length === 0 && legacyPatients.length > 0) {
        loaded = await migrateLegacyPatients(legacyPatients);
    }
    // Migration confirmée (ou données déjà en base) : la copie locale, qui contient
    // identité, adresse et téléphone, est retirée du poste (SEC-008).
    removeLegacyPatients();
    return loaded;
}

function useLoadPatients({ setPatients, setSelectedPatientId, setLoading, setSaveError }) {
    useEffect(() => {
        let cancelled = false;
        const show = loaded => {
            setPatients(loaded);
            setSelectedPatientId(loaded[0]?.id || "");
        };

        async function loadPatients() {
            setLoading(true);
            setSaveError("");
            try {
                const loaded = await fetchPatients();
                if (!cancelled) show(loaded);
            } catch {
                if (!cancelled) {
                    show(readLegacyPatients());
                    setSaveError("Impossible de joindre la base : affichage de la sauvegarde locale du navigateur.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadPatients();
        return () => {
            cancelled = true;
        };
    }, [setPatients, setSelectedPatientId, setLoading, setSaveError]);
}

// Fiches patients CNO : chargement, sélection, états d'enregistrement et identité.
export default function useCnoPatients() {
    const [patients, setPatients] = useState([]);
    const [selectedPatientId, setSelectedPatientId] = useState("");
    const [loading, setLoading] = useState(true);
    const [saveError, setSaveError] = useState("");
    const [saveStatuses, setSaveStatuses] = useState({});

    useLoadPatients({ setPatients, setSelectedPatientId, setLoading, setSaveError });

    useEffect(() => {
        setSelectedPatientId(current => reconcileSelectedPatientId(patients, current));
    }, [patients]);

    const selectedPatient = useMemo(
        () => patients.find(patient => patient.id === selectedPatientId) || null,
        [patients, selectedPatientId]
    );

    const markStatus = (key, status) => setSaveStatuses(current => withStatus(current, key, status));

    const createPatient = async fields => {
        const patient = emptyPatient(fields);
        const localKey = saveKeyForPatient(patient.id);
        setSaveError("");
        markStatus(localKey, "saving");
        setPatients(current => [patient, ...current]);
        setSelectedPatientId(patient.id);
        try {
            const response = await createCnoPatient({ ...fields, cnos: [] });
            const savedPatient = normalizePatients([response.data])[0];
            setPatients(current => replacePatient(current, patient.id, savedPatient));
            setSelectedPatientId(savedPatient.id);
            setSaveStatuses(current => replaceStatus(current, localKey, saveKeyForPatient(savedPatient.id), "saved"));
        } catch {
            markStatus(localKey, "error");
            setSaveError("Le patient est gardé localement, mais la sauvegarde base a échoué.");
        }
    };

    const updatePatient = patch => {
        if (!selectedPatientId) return;
        markStatus(saveKeyForPatient(selectedPatientId), "dirty");
        setPatients(current => patchPatient(current, selectedPatientId, patch));
    };

    const savePatient = (patient, patch = {}) => {
        if (!patient) return;
        const statusKey = saveKeyForPatient(patient.id);
        if (typeof patient.id !== "number") {
            markStatus(statusKey, "dirty");
            return;
        }
        markStatus(statusKey, "saving");
        updateCnoPatient(patient.id, { ...patient, ...patch })
            .then(response => {
                const savedPatient = normalizePatients([response.data])[0];
                setPatients(current => replacePatient(current, patient.id, savedPatient));
                markStatus(statusKey, "saved");
            })
            .catch(() => {
                markStatus(statusKey, "error");
                setSaveError("La modification patient n'a pas pu être sauvegardée en base.");
            });
    };

    return {
        patients, setPatients, selectedPatientId, setSelectedPatientId, selectedPatient,
        loading, saveError, setSaveError, saveStatuses, setSaveStatuses, markStatus,
        createPatient, updatePatient, savePatient,
    };
}
